import React, { useContext, useEffect, useState } from 'react';
import { css } from 'linaria';
import t from '@jetshop/intl';
import Popup from '../ui/Popup';
import SiteContentContext from '../SiteContent/SiteContentContext';
import { theme } from '../Theming/Theming';
import { useB2BChannel } from '../Layout/Header/ChannelSelector/useB2BChannel';
import { useCustomerTypePopup } from './useCustomerTypePopup';

const OPEN_DELAY_MS = 700;
const DEFAULT_RETAILER_URL = 'https://mrplant-retail.com/';

//OVERLAY GETS ITS OWN CLASS SO THE WHOLE POPUP CAN FADE IN
const overlayStyle = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: 0;
  animation-name: fadeIn;
  animation-duration: 400ms;
  animation-fill-mode: forwards;

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

const popupStyle = css`
  ${theme.above.md} {
    width: 760px;
  }
  .popup-inner {
    background: ${theme.colors.vanillaWhite};
    padding: 2.5rem 1.5rem 1.5rem;
    ${theme.above.md} {
      padding: 3rem 2.5rem 2.5rem;
    }
  }
  .customer-type-heading {
    text-align: center;
    margin-bottom: 1.5rem;
    font-size: 1.6rem;
    color: ${theme.colors.font};
  }
  .customer-type-options {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    ${theme.above.md} {
      flex-direction: row;
    }
  }
  .customer-type-option {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    gap: 0.5rem;
    min-height: 72px;
    padding: 1rem;
    background: ${theme.colors.white};
    border: 1px solid ${theme.colors.primary};
    color: ${theme.colors.font};
    text-decoration: none;
    cursor: pointer;
    transition: all 200ms;
    &:hover,
    &:focus-visible {
      border-color: ${theme.colors.primaryDark};
      background: ${theme.colors.primaryDark};
      color: ${theme.colors.white};
    }
  }
  .customer-type-title {
    font-weight: ${theme.weights.bold};
    font-size: 1.1rem;
  }
  .customer-type-text {
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

const CustomerTypePopup = () => {
  const {
    customerTypePopupHeading,
    customerTypePopupPrivateText,
    customerTypePopupRetailerText,
    customerTypePopupRetailerUrl,
    customerTypePopupCompanyText,
  } = useContext(SiteContentContext) || {};
  const [isOpen, setIsOpen] = useState(false);
  const { goToChannel } = useB2BChannel();
  const { shouldShow, saveDismissCookie } = useCustomerTypePopup();

  //ONLY RUNS IN THE BROWSER, SO THE POPUP IS NEVER PART OF THE SERVER RENDERED HTML
  useEffect(() => {
    if (!shouldShow) return;
    const timer = setTimeout(() => setIsOpen(true), OPEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [shouldShow]);

  //COMPANY IS THE B2B CHANNEL IN THIS SHOP, SO WE SWITCH CHANNEL LIKE THE HEADER DOES
  const goToCompanyChannel = () => {
    saveDismissCookie();
    goToChannel();
  };

  const closePopup = () => {
    saveDismissCookie();
    setIsOpen(false);
  };

  if (!shouldShow && !isOpen) return null;

  return (
    <Popup
      isOpen={isOpen}
      closePopup={closePopup}
      className={popupStyle}
      overlayClassName={overlayStyle}
      modalStyles={{ overlay: {}, content: {} }}
    >
      <h2 className="customer-type-heading">
        {customerTypePopupHeading || t('How do you shop with us?')}
      </h2>
      <div className="customer-type-options">
        <button className="customer-type-option" onClick={closePopup}>
          <span className="customer-type-title">{t('Private customer')}</span>
          <span className="customer-type-text">
            {customerTypePopupPrivateText ||
              t('For you who shop plants and interior décor for your home')}
          </span>
        </button>
        {/* NO DISMISS COOKIE HERE, RETAILERS SHOULD SEE THE POPUP AGAIN NEXT TIME */}
        <a
          className="customer-type-option"
          href={customerTypePopupRetailerUrl || DEFAULT_RETAILER_URL}
          rel="nofollow noopener"
        >
          <span className="customer-type-title">{t('Retailer')}</span>
          <span className="customer-type-text">
            {customerTypePopupRetailerText ||
              t('For contract customers who order via our retailer portal')}
          </span>
        </a>
        <button className="customer-type-option" onClick={goToCompanyChannel}>
          <span className="customer-type-title">{t('Company')}</span>
          <span className="customer-type-text">
            {customerTypePopupCompanyText ||
              t('Single purchases for the office or business, on invoice')}
          </span>
        </button>
      </div>
    </Popup>
  );
};

export default CustomerTypePopup;
